/**
 * AiAssistant - Phase 1 關鍵字搜索 AI 助理
 *
 * 架構：
 * - 知識庫以 Q&A 陣列形式內嵌（從 docs/ai-kb/*.md 提取）
 * - 關鍵字匹配 → 回傳最相關的 Q&A
 * - 不依賴任何外部模型，Phase 2 替換搜索引擎即可
 *
 * 使用：
 *   const ai = new AiAssistant({ role: 'trader' }); // 'trader' | 'broker' | 'admin'
 *   ai.mount();
 */

export interface AiAssistantOptions {
  role: 'trader' | 'broker' | 'admin';
  position?: 'bottom-right' | 'bottom-left';
}

interface QA {
  q: string;
  a: string;
  tags: string[];
  roles: ('trader' | 'broker' | 'admin')[];
}

// ─────────────────────────────────────────────────────────
// 知識庫（從 docs/ai-kb/*.md 提取，Phase 2 改為動態載入）
// ─────────────────────────────────────────────────────────
const KB: QA[] = [
  // === 通用帳戶 ===
  {
    q: '餘額/balance 是什麼意思',
    a: `帳戶數字說明：
• 總餘額（balance）：帳戶總資金
• 可用餘額（availableBalance）：可下新單的資金 = balance - frozenMargin + unrealizedPnl
• 凍結保證金（frozenMargin）：已開倉佔用的保證金
• 未實現盈虧（unrealizedPnl）：持倉中的浮動盈虧
• 淨值（netValue）：帳戶實際價值 = balance + unrealizedPnl`,
    tags: ['餘額', 'balance', '保證金', '盈虧', '淨值', '帳戶'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '如何存款',
    a: `存款方式：
點頁面頂部帳戶按鈕 → 存款 → 輸入金額確認。
注意：存款有手續費（預設 7%），實際到帳 = 金額 × 93%。`,
    tags: ['存款', 'deposit', '充值', '入金'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '如何取款',
    a: `取款方式：
點頁面頂部帳戶按鈕 → 取款 → 輸入金額確認。`,
    tags: ['取款', 'withdraw', '出金', '提款'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '爆倉/強平 條件',
    a: `爆倉（強制平倉）觸發條件：
帳戶保證金比例 ≤ 5%
保證金比例 = 凍結保證金 / 淨值

例：保證金 10000，淨值只剩 400 → 比例 4% → 觸發強平。
強平後所有倉位自動關閉，保證金歸零。`,
    tags: ['爆倉', '強平', '強制平倉', '清算', 'liquidation', '保證金比例'],
    roles: ['trader', 'broker', 'admin'],
  },

  // === 下單操作 ===
  {
    q: '訂單類型有哪些',
    a: `訂單類型：
• 市價單：立即以當前最優價格成交
• 限價單：設定目標價格，到價才成交
• 止損單：設定觸發價，到價後以市價開倉
• 流動性訂單：做市商雙邊掛單，買+賣自動配對
• 訂單牆（Level 0）：最優先位置的流動性訂單`,
    tags: ['訂單', '類型', '市價', '限價', '止損', '流動性'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '如何下市價單',
    a: `市價單操作：
下單面板 → 選「市價」→ 設定槓桿、數量 → 點「買入」或「賣出」。
市價單立即以當前最優價格成交。`,
    tags: ['市價單', 'market order', '下單', '買入', '賣出'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '如何下限價單',
    a: `限價單操作：
下單面板 → 選「限價」→ 輸入委託價格、數量、槓桿 → 點「買入」或「賣出」。
限價單在價格到達時才成交，等待期間保證金已凍結。`,
    tags: ['限價單', 'limit order', '委託', '掛單'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: 'OTOCO 止損止盈 如何設定',
    a: `OTOCO（下單自帶止損止盈）：
下單時勾選「止損/止盈」→ 輸入觸發價格 → 下單後自動建立 OCO 條件單。

規則：
• 止盈觸發 → 自動平倉 + 止損連動撤銷
• 止損觸發 → 自動平倉 + 止盈連動撤銷
• 兩者只會觸發其中一個`,
    tags: ['止損', '止盈', 'OTOCO', 'OCO', '保護', 'stop loss', 'take profit'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '槓桿怎麼計算',
    a: `槓桿與保證金：
所需保證金 = 數量 × 價格 / 槓桿

例：買 0.01 BTC，價格 70000，槓桿 10x
→ 所需保證金 = 0.01 × 70000 / 10 = 70 USDT

槓桿越高，保證金越少，但風險越大。`,
    tags: ['槓桿', 'leverage', '保證金計算', '倉位'],
    roles: ['trader', 'broker', 'admin'],
  },

  // === 訂單管理 ===
  {
    q: '訂單狀態是什麼意思',
    a: `訂單狀態說明：
• pending：掛單中，等待成交
• filled：完全成交，持倉中
• partially_filled：部分成交
• partially_closed：已部分平倉
• closed：完全平倉，已結算
• cancelled：已撤銷
• rejected：被系統拒絕`,
    tags: ['訂單狀態', 'status', 'pending', 'filled', 'cancelled', 'closed'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '如何平倉',
    a: `平倉方式：
底部訂單列表 → 找到持倉訂單 → 點「平倉」。
或點「全部平倉」一次關掉同商品所有倉位。
平倉走市價單，立即成交。`,
    tags: ['平倉', 'close', '關倉', '結算'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '如何撤銷訂單',
    a: `撤單方式：
底部面板 → 找到 pending 狀態的訂單 → 點「撤單」。
撤單後保證金自動釋放，回到可用餘額。`,
    tags: ['撤單', 'cancel', '取消訂單', '撤銷'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '盈虧怎麼計算',
    a: `盈虧計算公式：
• 多單（買入）：盈虧 = (當前價 - 開倉價) × 數量
• 空單（賣出）：盈虧 = (開倉價 - 當前價) × 數量

手續費另計（在平倉時扣除 Taker 費）。`,
    tags: ['盈虧', 'PnL', '計算', '利潤', '虧損'],
    roles: ['trader', 'broker', 'admin'],
  },

  // === 流動性訂單 ===
  {
    q: '流動性訂單是什麼',
    a: `流動性訂單（做市商掛單）：
在訂單簿掛真實雙邊掛單（買+賣），提供市場深度。

特點：
• 扣真實保證金
• 任何角色（trader/broker）都可以使用
• 成交後有退佣收益
• 任一邊成交 → 另一邊自動撤銷（連動撤）`,
    tags: ['流動性', 'liquidity', '做市商', '雙邊', '掛單'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '流動性訂單 Level 是什麼',
    a: `Level 說明（流動性訂單深度）：
• Level 1：最接近市場價，成交機率最高，spread 最小
• Level 5：距市場價較遠，成交機率低，spread 較大
• Level 0（訂單牆）：最優先位置，價格 = Level 1 ± 1 tick

建議：高成交率 → Level 1-2；追求大 spread → Level 5+`,
    tags: ['Level', '檔位', '流動性', '訂單牆', '深度'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '流動性訂單 TTL 是什麼',
    a: `TTL（存活時間）：
無市場報價超過 TTL 秒數，訂單自動撤銷。

例：TTL=5，商品 5 秒沒有報價 → 訂單消失。
有報價時 TTL 重置，持續掛著。`,
    tags: ['TTL', '存活時間', '流動性', '自動撤單'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '批量掛單怎麼用',
    a: `批量掛單：
chart.html 頂部 → 點「批量掛單」按鈕。

功能：
• 多組不同 Level/百分比同時掛出
• 每組各自設定數量和 TTL
• 自動重置：成交後冷卻 N 秒重新掛（適合長時間做市）
• 模板儲存/載入`,
    tags: ['批量', '掛單', 'batch', '自動重置', '做市'],
    roles: ['trader', 'broker', 'admin'],
  },

  // === Broker 專屬 ===
  {
    q: '如何邀請客戶',
    a: `邀請客戶：
Broker Panel → 客戶管理 Tab → 複製「註冊/登入鏈接」。
格式：/pages/unified-login.html#brokerUsername

客戶點連結註冊後，自動綁定到你的帳號下。`,
    tags: ['邀請', '客戶', '推薦連結', '註冊', 'invite'],
    roles: ['broker', 'admin'],
  },
  {
    q: '佣金怎麼設定和結算',
    a: `佣金設定：
Broker Panel → 佣金 Tab：
1. 建立 Tier（等級名稱 + 費率）
2. 分配客戶到 Tier，或直接設個別費率

費率方向：
• 正數（如 0.1）= 向客戶收取 10%
• 負數（如 -0.05）= 退佣 5% 給客戶

結算：找到客戶 → 點「結算」→ 確認後雙方帳戶自動更新。`,
    tags: ['佣金', 'commission', '結算', 'tier', '退佣'],
    roles: ['broker', 'admin'],
  },
  {
    q: '對沖引擎怎麼開',
    a: `對沖設定：
Broker Panel → 客戶管理 → 全局設置 → 設對沖比例 → 儲存。

說明：
• 對沖比例 75% = 對 75% 的客戶倉位建反向對沖
• 只有客戶打到「虛擬訂單」時才觸發
• 打到流動性訂單不觸發對沖`,
    tags: ['對沖', 'hedge', 'B-Book', '對沖比例', '引擎'],
    roles: ['broker', 'admin'],
  },

  // === Admin 專屬 ===
  {
    q: '後端重啟怎麼做',
    a: `重啟後端服務：
\`\`\`
pm2 restart sim-backend --update-env
\`\`\`
注意：必須加 --update-env，否則環境變數不更新。`,
    tags: ['重啟', 'restart', 'pm2', '後端', 'backend'],
    roles: ['admin'],
  },
  {
    q: 'isEnabled 和 isActive 有什麼差別',
    a: `商品的兩個開關：
• isEnabled（13個）：開啟撮合引擎、訂單、費率計算
• isActive（446個）：開啟報價推送、前端商品列表顯示

重啟後端若沒有報價 → 檢查 isActive 商品數量是否正確。`,
    tags: ['isEnabled', 'isActive', '商品', '報價', '撮合'],
    roles: ['admin'],
  },
  {
    q: '費率繼承邏輯',
    a: `費率優先級（由高到低）：
1. Broker 為客戶設定的個別費率
2. Broker 的全局費率
3. 系統默認費率

Broker 唯讀，不能改系統費率，只有 admin 能改。`,
    tags: ['費率', 'fee rate', 'maker', 'taker', '繼承'],
    roles: ['admin'],
  },

  // === 常見錯誤 ===
  {
    q: '下單失敗 可用保證金不足',
    a: `錯誤：可用保證金不足

原因：帳戶可用餘額不夠支付開倉所需保證金。

解決方式：
1. 減少下單數量
2. 降低槓桿（槓桿越低，需要保證金越多）
3. 先平掉部分舊倉位，釋放保證金
4. 存款增加帳戶餘額

計算公式：所需保證金 = 數量 × 價格 / 槓桿`,
    tags: ['保證金不足', '下單失敗', '可用', 'insufficient', '失敗'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '限價單沒有成交',
    a: `限價單沒成交的原因：

• 買入限價單：需要市場價「跌到」你設定的價格以下才成交
• 賣出限價單：需要市場價「漲到」你設定的價格以上才成交

如果價格一直沒到達，訂單會持續掛著（status=pending）。
可以在底部面板查看訂單狀態，隨時可以撤銷。`,
    tags: ['限價單', '沒成交', '未成交', '等待', 'pending'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '訂單 rejected 被拒絕',
    a: `訂單被拒絕（rejected）常見原因：

1. 數量低於最小下單量（通常 0.001）
2. 商品暫停交易（isEnabled=false）
3. 帳戶保證金不足
4. 槓桿超過允許範圍
5. TTL 設定無效（流動性訂單）

建議：檢查下單參數，確認商品有開放交易。`,
    tags: ['rejected', '被拒絕', '拒絕', '訂單失敗', 'error'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '為什麼看不到訂單簿',
    a: `訂單簿無法顯示的原因：

1. 商品沒有市場報價（isActive=false 或 Binance WS 斷線）
2. 網路連接問題
3. 商品代碼錯誤

解決方式：
• 嘗試切換其他商品
• 重新整理頁面
• 確認網路正常

如果持續無法顯示，聯繫管理員確認後端服務狀態。`,
    tags: ['訂單簿', 'orderbook', '看不到', '沒有', '空白', '報價'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '手續費怎麼收',
    a: `手續費說明：

• Maker（掛單方）：費率較低，通常接近 0%
  → 掛限價單、流動性訂單等等待成交的訂單
• Taker（吃單方）：費率較高，通常 0.02%-0.1%
  → 市價單、主動成交的訂單

費用在成交時自動扣除。
具體費率由 Broker 設定，最終由 Admin 系統費率決定上限。`,
    tags: ['手續費', 'fee', 'maker', 'taker', '費用', '費率'],
    roles: ['trader', 'broker', 'admin'],
  },

  // === Admin SOP ===
  {
    q: '新用戶無法登入',
    a: `排查步驟：
1. 確認帳號存在（admin.html → 用戶管理）
2. 確認密碼正確（可重設密碼）
3. 確認後端服務正常：pm2 list
4. 確認 JWT secret 一致（開發環境應為 dev-jwt-secret）
5. 查看後端日誌：tail -f ~/.pm2/logs/sim-backend-out.log`,
    tags: ['登入', '無法登入', '帳號', '密碼', 'login', '故障排除'],
    roles: ['admin'],
  },
  {
    q: '後端沒有報價 市場價格不更新',
    a: `排查步驟：
1. 確認後端運行：pm2 list（sim-backend 應為 online）
2. 確認 isActive 商品數量：應有 446 個
3. 查看日誌確認 MarketDataService 是否連上 Binance WS
4. 如果 WS 斷線，重啟後端會自動重連：
   pm2 restart sim-backend --update-env`,
    tags: ['報價', '市場價格', 'market price', '不更新', 'WebSocket', 'WS', 'Binance'],
    roles: ['admin'],
  },
  {
    q: '如何查看系統日誌',
    a: `查看後端日誌：
# 即時日誌
pm2 logs sim-backend

# 最近 100 行
tail -100 ~/.pm2/logs/sim-backend-out.log

# 錯誤日誌
tail -100 ~/.pm2/logs/sim-backend-error.log

# 前端日誌（vite）
pm2 logs sim-frontend`,
    tags: ['日誌', 'log', 'logs', '錯誤', 'error', '查看'],
    roles: ['admin'],
  },
  {
    q: '如何備份數據庫',
    a: `備份 SQLite 資料庫：
cp /opt/sim-trading-pro/backend/prisma/dev.db /root/backup-$(date +%Y%m%d-%H%M).db

建議在重要操作前先備份（如批量調整用戶餘額、修改費率等）。

查看備份：ls -la /root/backup-*.db`,
    tags: ['備份', 'backup', '資料庫', 'database', 'SQLite', 'DB'],
    roles: ['admin'],
  },
  {
    q: '如何調整用戶餘額',
    a: `調整用戶餘額：
admin.html → 用戶管理 → 找到該用戶 → 點「調整餘額」

或透過 API：
POST /api/admin/users/:userId/adjust-balance
{
  "amount": 1000,
  "type": "deposit",
  "notes": "管理員調整"
}

type 可以是：deposit（增加）/ withdrawal（減少）`,
    tags: ['調整餘額', 'adjust', '餘額', 'balance', '存款', '管理員'],
    roles: ['admin'],
  },

  // === Broker 進階 ===
  {
    q: '流動性訂單退佣怎麼算',
    a: `流動性訂單退佣計算：

退佣 = 成交金額 × (Taker費率 - Maker費率) × 70%

例：Taker=0.05%，Maker=0%，成交 1 BTC @ 70000 USDT
退佣 = 70000 × 0.0005 × 0.7 = 24.5 USDT

查看退佣：底部面板 → 流動性 Tab → 收益統計`,
    tags: ['退佣', 'rebate', '流動性', '收益', '計算'],
    roles: ['broker', 'admin'],
  },
  {
    q: 'Market Maker Reprice 重新定價',
    a: `Market Maker Reprice API：

重新定價已掛的配對訂單，不需要手動撤單重掛。

API：POST /api/liquidity/reprice
{
  "pairId": "pair_xxx",
  "newBidPrice": 68000,
  "newAskPrice": 72000
}

注意：
• bid 必須 < ask，否則 400
• reprice 後 pairId 會改變（從 response 拿新 pairId）
• 若 pair 已成交（無 pending），返回 400`,
    tags: ['reprice', '重新定價', 'pairId', 'market maker', '做市商'],
    roles: ['broker', 'admin'],
  },
  {
    q: '部分平倉怎麼操作',
    a: `部分平倉操作：
底部訂單列表 → 找到持倉訂單 → 點「減量」→ 輸入要平倉的數量。

剩餘部分繼續持倉，訂單狀態變為 partially_closed。

注意：部分平倉後，保證金按比例釋放。`,
    tags: ['部分平倉', '減量', 'partial close', '減倉', '部分'],
    roles: ['trader', 'broker', 'admin'],
  },
  {
    q: '反向開倉是什麼',
    a: `反向開倉：
在相反方向以同等數量開新倉，等同「平倉 + 立即反向開倉」。

操作：底部訂單列表 → 找到持倉訂單 → 點「反向」。

適合想快速轉換方向時使用。`,
    tags: ['反向', 'reverse', '反向開倉', '轉向', '做空', '做多'],
    roles: ['trader', 'broker', 'admin'],
  },
];

// ─────────────────────────────────────────────────────────
// 快捷問題（按角色顯示）
// ─────────────────────────────────────────────────────────
const QUICK_QUESTIONS: Record<string, string[]> = {
  trader: ['餘額是什麼意思', '如何下市價單', '如何平倉', '爆倉條件', '流動性訂單是什麼', '手續費怎麼收', '訂單 rejected'],
  broker: ['如何邀請客戶', '佣金怎麼設定', '流動性訂單 Level', '對沖引擎怎麼開', '批量掛單怎麼用', '退佣怎麼算'],
  admin: ['後端重啟怎麼做', '沒有報價怎麼辦', '費率繼承邏輯', '如何備份資料庫', '如何查看日誌', '爆倉條件'],
};

// ─────────────────────────────────────────────────────────
// 主類
// ─────────────────────────────────────────────────────────
export class AiAssistant {
  private role: 'trader' | 'broker' | 'admin';
  private _personasCache: any[] = [];
  private _currentPersonaId: string | null = null;
  private panel: HTMLElement | null = null;
  private messagesEl: HTMLElement | null = null;
  private inputEl: HTMLInputElement | null = null;
  private isOpen = false;
  // 對話歷史（最多保留 20 輪）
  private history: { role: string; content: string }[] = [];
  private indicatorManager: any = null;
  private subChartManager: any = null;
  private chartManager: any = null;
  constructor(options: AiAssistantOptions) {
    this.role = options.role;
  }

  mount(): void {
    this.injectStyles();
    this.createBtn();
    this.createPanel();
    this._loadPersonas();
    this.sendBotMsg(this.getWelcome());
    this.renderQuickBtns();
  }

  public setManagers(indicatorManager: any, subChartManager: any, chartManager: any): void {
    this.indicatorManager = indicatorManager;
    this.subChartManager = subChartManager;
    this.chartManager = chartManager;
  }


  // 公開方法：套用指定人設（由會員 Modal 呼叫）
  public applyPersona(personaId: string): void {
    if (!personaId) {
      // 遂到默認
      const icon = this.panel?.querySelector('#ai-persona-icon');
      if (icon) icon.textContent = '🤖';
      const btn = document.getElementById('ai-assistant-btn');
      if (btn) btn.style.background = 'linear-gradient(135deg,#58a6ff,#1f6feb)';
      this._currentPersonaId = null;
      return;
    }
    const p = this._personasCache.find((x: any) => x.id === personaId);
    if (p) {
      this._currentPersonaId = personaId;
      const icon = this.panel?.querySelector('#ai-persona-icon');
      if (icon) icon.textContent = p.emoji || '🤖';
      const btn = document.getElementById('ai-assistant-btn');
      if (btn) {
        const c1 = p.color || p.colorStart || '#58a6ff';
        const c2 = p.colorEnd || c1;
        const shadow = p.shadowColor || 'rgba(88,166,255,0.4)';
        btn.style.background = `linear-gradient(135deg,${c1},${c2})`;
        btn.style.boxShadow = `0 4px 12px ${shadow}`;
      }
    }
  }

  private async _loadPersonas(): Promise<void> {
    try {
      const token = localStorage.getItem('auth_token') || '';
      const res = await fetch('/api/ai/personas', {
        headers: token ? { 'Authorization': 'Bearer ' + token } : {}
      });
      if (!res.ok) return;
      const data = await res.json();
      this._personasCache = Array.isArray(data) ? data : (data.personas || []);
      if (this._personasCache.length === 0) return;

      // 更新 panel 內的人設下拉選單
      const panelSel = document.getElementById('ai-panel-persona-sel') as HTMLSelectElement | null;
      if (panelSel) {
        panelSel.innerHTML = '<option value="">🤖 預設</option>';
        this._personasCache.forEach((p: any) => {
          const opt = document.createElement('option');
          opt.value = p.id;
          opt.textContent = `${p.emoji || '🤖'} ${p.name}`;
          panelSel.appendChild(opt);
        });
        panelSel.addEventListener('change', () => {
          const pid = panelSel.value;
          localStorage.setItem('ai-persona-id', pid);
          this.applyPersona(pid);
        });
      }

      // 套用儲存的人設
      const saved = localStorage.getItem('ai-persona-id') || '';
      if (saved) {
        this.applyPersona(saved);
        if (panelSel) panelSel.value = saved;
      }
    } catch (e) {
      console.warn('[AiAssistant] Failed to load personas:', e);
    }
  }


  public openWithMessage(msg: string): void {
    this.open();
    if (msg) {
      setTimeout(() => {
        if (this.inputEl) {
          // 只填入，讓用戶自己按發送，不自動提交
          this.inputEl.value = msg;
          this.inputEl.focus();
          this.inputEl.setSelectionRange(0, this.inputEl.value.length);
        }
      }, 150);
    }
  }

  private getWelcome(): string {
    const greet: Record<string, string> = {
      trader: '👋 你好！我是交易助理，可以幫你解答下單、平倉、止損止盈等操作問題。',
      broker: '👋 你好！我是 Broker 助理，可以幫你解答客戶管理、佣金、流動性做市等問題。',
      admin: '👋 你好！我是系統助理，可以幫你解答系統管理、費率、商品配置等問題。',
    };
    return greet[this.role] + '\n\n點下方快捷問題，或直接輸入你的問題。';
  }

  private createBtn(): void {
    const btn = document.createElement('button');
    btn.id = 'ai-assistant-btn';
    btn.title = 'AI 助理（可拖曳移動）';
    btn.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;
    // 強制 fixed 定位
    btn.style.position = "fixed";
    btn.style.zIndex = "9990";
    btn.style.width = "48px";
    btn.style.height = "48px";
    btn.style.borderRadius = "50%";
    btn.style.border = "none";
    btn.style.background = "linear-gradient(135deg,#58a6ff,#1f6feb)";
    btn.style.color = "#fff";
    btn.style.cursor = "pointer";
    btn.style.display = "flex";
    btn.style.alignItems = "center";
    btn.style.justifyContent = "center";
    btn.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)";
    document.body.appendChild(btn);

    // 恢復上次位置（bottom/right 太小視為無效，重置）
    const savedPos = localStorage.getItem('ai-btn-position');
    if (savedPos) {
      try {
        const { right, bottom } = JSON.parse(savedPos);
        if (typeof bottom !== 'number' || bottom < 20 || typeof right !== 'number' || right < 8) {
          // 舊位置無效，清掉讓 CSS 預設值生效
          localStorage.removeItem('ai-btn-position');
        } else {
          btn.style.right = right + 'px';
          btn.style.bottom = bottom + 'px';
          btn.style.left = 'auto';
          btn.style.top = 'auto';
        }
      } catch {}
    }

    // 拖曳邏輯
    let isDragging = false;
    let startX = 0, startY = 0;
    let startRight = 0, startBottom = 0;
    let dragMoved = false;

    btn.addEventListener('mousedown', (e) => {
      isDragging = true;
      dragMoved = false;
      startX = e.clientX;
      startY = e.clientY;
      const rect = btn.getBoundingClientRect();
      startRight = window.innerWidth - rect.right;
      startBottom = window.innerHeight - rect.bottom;
      btn.style.transition = 'none';
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragMoved = true;
      const newRight = Math.max(8, Math.min(window.innerWidth - 56, startRight - dx));
      const newBottom = Math.max(8, Math.min(window.innerHeight - 56, startBottom - dy));
      btn.style.right = newRight + 'px';
      btn.style.bottom = newBottom + 'px';
      btn.style.left = 'auto';
      btn.style.top = 'auto';
    });

    document.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      btn.style.transition = '';
      // 保存位置
      const right = parseFloat(btn.style.right) || 24;
      const bottom = parseFloat(btn.style.bottom) || 24;
      localStorage.setItem('ai-btn-position', JSON.stringify({ right, bottom }));
      // 同步更新面板位置
      const panel = document.getElementById('ai-assistant-panel');
      if (panel) {
        panel.style.right = right + 'px';
        panel.style.bottom = (bottom + 60) + 'px';
      }
    });

    // 點擊（非拖曳時才 toggle）
    btn.addEventListener('click', () => {
      if (!dragMoved) this.toggle();
    });
  }

  private createPanel(): void {
    const panel = document.createElement('div');
    panel.id = 'ai-assistant-panel';
    panel.className = 'hidden';
    panel.innerHTML = `
      <div class="ai-panel-header">
        <div class="ai-panel-title">
          <span id="ai-persona-icon">🤖</span>
          <span>AI 助理</span>
          <span style="font-size:11px; color:#8b949e; font-weight:400; margin-left:4px;">${this.roleLabel()}</span>
        </div>
        <button class="ai-panel-expand" id="ai-panel-expand" title="放大/縮小">⤢</button>
        <button class="ai-panel-close" id="ai-panel-close">✕</button>
      </div>
      <div class="ai-persona-bar" style="display:flex;align-items:center;gap:6px;padding:6px 12px;border-bottom:1px solid var(--border-color,#30363d);background:var(--bg-secondary,#161b22);">
        <span style="font-size:11px;color:#8b949e;white-space:nowrap;">人設：</span>
        <select id="ai-panel-persona-sel" style="flex:1;padding:4px 6px;background:var(--bg-primary,#0d1117);border:1px solid var(--border-color,#30363d);border-radius:4px;color:var(--text-primary,#e6edf3);font-size:11px;">
          <option value="">🤖 預設</option>
        </select>
      </div>
      <div class="ai-messages" id="ai-messages"></div>
      <div class="ai-quick-btns" id="ai-quick-btns"></div>
      <div class="ai-input-row">
        <input class="ai-input" id="ai-input" type="text" placeholder="輸入問題…" autocomplete="off" />
        <button class="ai-send-btn" id="ai-send-btn">發送</button>
      </div>
    `;
    // 強制 fixed 定位，確保在所有頁面都浮動顯示
    panel.style.cssText = `
      position: fixed !important;
      bottom: 84px !important;
      right: 24px !important;
      width: 460px;
      max-height: 640px;
      background: var(--bg-secondary, #161b22);
      border: 1px solid var(--border-color, #30363d);
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.5);
      z-index: 9989 !important;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    `;
    document.body.appendChild(panel);

    this.panel = panel;
    this.messagesEl = panel.querySelector('#ai-messages');
    this.inputEl = panel.querySelector('#ai-input');

    panel.querySelector('#ai-panel-close')!.addEventListener('click', () => this.close());
    panel.querySelector('#ai-panel-expand')!.addEventListener('click', () => this.toggleExpand());
    panel.querySelector('#ai-send-btn')!.addEventListener('click', () => this.handleSend());
    this.inputEl!.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.handleSend();
    });
  }


  private injectStyles(): void {
    if (document.getElementById("ai-assistant-styles")) return;
    const style = document.createElement("style");
    style.id = "ai-assistant-styles";
    style.textContent = `
#ai-assistant-btn{position:fixed;bottom:24px;right:24px;width:48px;height:48px;border-radius:50%;background:linear-gradient(135deg,#58a6ff,#1f6feb);border:none;cursor:pointer;font-size:20px;box-shadow:0 4px 16px rgba(88,166,255,0.4);z-index:9990;display:flex;align-items:center;justify-content:center;transition:transform .2s,box-shadow .2s}
#ai-assistant-btn:hover{transform:scale(1.1);box-shadow:0 6px 20px rgba(88,166,255,0.6)}
#ai-assistant-panel{position:fixed;bottom:84px;right:24px;width:460px;max-height:640px;background:var(--bg-secondary,#161b22);border:1px solid var(--border-color,#30363d);border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,.5);z-index:9989;display:flex;flex-direction:column;overflow:hidden;transition:opacity .2s,transform .2s}
#ai-assistant-panel.hidden{opacity:0;pointer-events:none;transform:translateY(10px)}
.ai-panel-header{padding:12px 16px;background:var(--bg-primary,#0d1117);border-bottom:1px solid var(--border-color,#30363d);display:flex;align-items:center;justify-content:space-between}
.ai-panel-title{font-size:14px;font-weight:600;color:var(--text-primary,#c9d1d9);display:flex;align-items:center;gap:8px}
.ai-panel-close,.ai-panel-expand{background:none;border:none;color:var(--text-secondary,#8b949e);cursor:pointer;font-size:16px;padding:2px 6px;border-radius:4px}
.ai-panel-close:hover,.ai-panel-expand:hover{color:var(--text-primary,#c9d1d9);background:var(--bg-tertiary,#21262d)}
.ai-messages{flex:1;overflow-y:auto;padding:12px;display:flex;flex-direction:column;gap:10px;min-height:320px}
.ai-messages::-webkit-scrollbar{width:4px}
.ai-messages::-webkit-scrollbar-thumb{background:var(--border-color,#30363d);border-radius:2px}
.ai-msg{max-width:85%;padding:8px 12px;border-radius:10px;font-size:13px;line-height:1.5;white-space:pre-wrap;word-break:break-word}
.ai-msg.bot{background:var(--bg-tertiary,#21262d);color:var(--text-primary,#c9d1d9);align-self:flex-start;border-radius:10px 10px 10px 2px}
.ai-msg.user{background:linear-gradient(135deg,#1f6feb,#388bfd);color:#fff;align-self:flex-end;border-radius:10px 10px 2px 10px}
.ai-msg.bot a{color:#58a6ff;text-decoration:none}
.ai-quick-btns{padding:8px 12px;display:flex;flex-wrap:wrap;gap:6px;border-top:1px solid var(--border-color,#30363d)}
.ai-quick-btn{padding:4px 10px;background:var(--bg-tertiary,#21262d);border:1px solid var(--border-color,#30363d);border-radius:16px;color:var(--text-secondary,#8b949e);font-size:11px;cursor:pointer;white-space:nowrap;transition:all .15s}
.ai-quick-btn:hover{background:var(--border-color,#30363d);color:var(--text-primary,#c9d1d9)}
.ai-input-row{display:flex;gap:8px;padding:10px 12px;border-top:1px solid var(--border-color,#30363d);background:var(--bg-primary,#0d1117)}
.ai-input{flex:1;background:var(--bg-tertiary,#21262d);border:1px solid var(--border-color,#30363d);border-radius:8px;color:var(--text-primary,#c9d1d9);font-size:13px;padding:7px 12px;outline:none;transition:border-color .15s}
.ai-input:focus{border-color:#58a6ff}
.ai-input::placeholder{color:var(--text-secondary,#8b949e)}
.ai-send-btn{padding:7px 14px;background:#1f6feb;border:none;border-radius:8px;color:#fff;font-size:13px;cursor:pointer;transition:background .15s}
.ai-send-btn:hover{background:#388bfd}
#ai-assistant-panel.ai-expanded{width:780px;max-height:85vh;bottom:20px;right:20px}
#ai-assistant-panel.ai-expanded .ai-messages{min-height:500px}
@media(max-width:480px){#ai-assistant-panel{right:8px;left:8px;width:auto;bottom:76px}}
    `;
    document.head.appendChild(style);
  }

  private roleLabel(): string {
    return { trader: 'Trader', broker: 'Broker', admin: 'Admin' }[this.role] || '';
  }

  private renderQuickBtns(): void {
    const container = this.panel?.querySelector('#ai-quick-btns');
    if (!container) return;
    const qs = QUICK_QUESTIONS[this.role] || [];
    container.innerHTML = qs.map(q =>
      `<button class="ai-quick-btn" data-q="${q}">${q}</button>`
    ).join('');
    container.querySelectorAll('.ai-quick-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const q = (btn as HTMLElement).dataset.q || '';
        this.handleQuestion(q);
      });
    });
  }

  private toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  private toggleExpand(): void {
    if (!this.panel) return;
    const isExpanded = this.panel.classList.contains('ai-expanded');
    if (isExpanded) {
      this.panel.classList.remove('ai-expanded');
      const btn = this.panel.querySelector('#ai-panel-expand') as HTMLElement;
      if (btn) btn.textContent = '⤢';
    } else {
      this.panel.classList.add('ai-expanded');
      const btn = this.panel.querySelector('#ai-panel-expand') as HTMLElement;
      if (btn) btn.textContent = '⤡';
    }
  }

  private open(): void {
    this.isOpen = true;
    this.panel?.classList.remove('hidden');
    this.inputEl?.focus();
    // 面板跟著按鈕位置
    const aiBtn = document.getElementById('ai-assistant-btn');
    if (aiBtn && this.panel) {
      const r = parseFloat(aiBtn.style.right) || 24;
      const b = parseFloat(aiBtn.style.bottom) || 24;
      this.panel.style.right = r + 'px';
      this.panel.style.bottom = (b + 60) + 'px';
    }
  }

  private close(): void {
    this.isOpen = false;
    this.panel?.classList.add('hidden');
  }

  private handleSend(): void {
    const q = this.inputEl?.value.trim();
    if (!q) return;
    this.inputEl!.value = '';
    this.handleQuestion(q);
  }

  private async handleQuestion(q: string): Promise<void> {
    this.sendUserMsg(q);

    // 記錄到歷史
    this.history.push({ role: 'user', content: q });
    if (this.history.length > 20) this.history = this.history.slice(-20);

    const loadingEl = this.appendLoading();
    try {
      const answer = await this.askAPI(q);
      loadingEl.remove();
      this.sendBotMsg(answer);
      // 記錄 AI 回答到歷史
      this.history.push({ role: 'assistant', content: answer });
    } catch {
      loadingEl.remove();
      const fallback = this.searchLocal(q);
      this.sendBotMsg(fallback);
      this.history.push({ role: 'assistant', content: fallback });
    }
  }

  private getUserId(): string {
    try {
      const session = JSON.parse(localStorage.getItem('current_session') || '{}');
      return session.uid || session.userId || session.id || '';
    } catch { return ''; }
  }

  private async askAPI(query: string): Promise<string> {
    const apiUrl = (window as any).API_BASE_URL ?? '';
    const token = (window as any).authApi?.getToken?.() ?? localStorage.getItem('auth_token') ?? '';
    const res = await fetch(`${apiUrl}/api/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message: query,
        history: this.history.slice(-10), // 最近 10 輪
        userId: this.getUserId(),
      }),
    });
    if (!res.ok) throw new Error('API error');
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'unknown');
    return data.answer;
  }

  private appendLoading(): HTMLElement {
    const div = document.createElement('div');
    div.className = 'ai-msg bot';
    div.innerHTML = '<span style="opacity:0.5">思考中…</span>';
    this.messagesEl?.appendChild(div);
    this.messagesEl!.scrollTop = this.messagesEl!.scrollHeight;
    return div;
  }

  private searchLocal(query: string): string {
    const q = query.toLowerCase().trim();

    // 過濾出當前角色可看的條目
    const available = KB.filter(item =>
      item.roles.includes(this.role) ||
      (this.role === 'broker' && item.roles.includes('trader')) ||
      (this.role === 'admin' && (item.roles.includes('trader') || item.roles.includes('broker')))
    );

    // 中文分詞（按字符切割，2-4字組合）
    const tokenize = (text: string): string[] => {
      const tokens: string[] = [];
      // 英文單詞
      const engWords = text.match(/[a-zA-Z0-9]+/g) || [];
      tokens.push(...engWords.map(w => w.toLowerCase()));
      // 中文字符 2-gram
      for (let i = 0; i < text.length - 1; i++) {
        if (/[\u4e00-\u9fff]/.test(text[i])) {
          tokens.push(text.slice(i, i + 2));
          if (i + 3 <= text.length) tokens.push(text.slice(i, i + 3));
        }
      }
      return tokens;
    };

    const qTokens = tokenize(q);

    // 計算相關分數
    const scored = available.map(item => {
      let score = 0;

      // 1. 完整 tags 精確匹配（最高權重）
      for (const tag of item.tags) {
        const t = tag.toLowerCase();
        if (q === t) { score += 10; continue; }
        if (q.includes(t) || t.includes(q)) score += 5;
      }

      // 2. tags token 匹配（高權重）
      const itemTagStr = item.tags.join(' ').toLowerCase();
      for (const token of qTokens) {
        if (token.length < 2) continue;
        if (itemTagStr.includes(token)) score += 3;
      }

      // 3. 問題文字匹配（中權重）
      const itemQ = item.q.toLowerCase();
      for (const token of qTokens) {
        if (token.length < 2) continue;
        if (itemQ.includes(token)) score += 2;
      }

      // 4. 答案文字匹配（低權重）
      const itemA = item.a.toLowerCase();
      for (const token of qTokens) {
        if (token.length < 2) continue;
        if (itemA.includes(token)) score += 0.5;
      }

      return { item, score };
    });

    const sorted = scored.sort((a, b) => b.score - a.score);
    const best = sorted[0];

    if (!best || best.score === 0) {
      return `抱歉，我沒有找到關於「${query}」的相關資訊。\n\n你可以嘗試：\n• 換個關鍵字（如「下單」、「平倉」、「保證金」）\n• 點下方快捷問題\n• 聯繫管理員`;
    }

    // 如果有第二名且分數接近，附加補充提示
    const second = sorted[1];
    let result = best.item.a;
    if (second && second.score >= best.score * 0.7 && second.score > 2) {
      result += `\n\n💡 相關問題：${second.item.q}`;
    }

    return result;
  }

  private sendBotMsg(text: string): void {
    this.appendMsg(text, 'bot');
  }

  private sendUserMsg(text: string): void {
    this.appendMsg(text, 'user');
  }

  private appendMsg(text: string, type: 'bot' | 'user'): void {
    if (!this.messagesEl) return;
    const div = document.createElement('div');
    div.className = `ai-msg ${type}`;
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }
}
